import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { USER_OPERATIONS, RESOURCE_TYPE, MESSAGE_TYPE } from "../../constants";
import styles from "./ShiftCapacity.module.css";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import CapacityForm from "../CapacityForm/CapacityForm";
import { getShiftLimitsPerPlant, updateShiftLimits } from "../../apis/calls";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";

const ShiftCapacity = ({ setHasActiveEdits, handleCountChange }) => {
  const ROW_HEIGHT = 38;

  const RESOURCE_OPERATION = USER_OPERATIONS.UPDATE_MASTER_LIMTS; 

  const { showError, showInternalError, showPopup } = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const [recordCount, setRecordCount] = useState(0);

  const [showForm, setShowForm] = useState(false);

  const [selectedShift, setSelectedShift] = useState(null);

  const setCount = (count) => {
    setRecordCount(count);
  };

  const fixedColumns = [
    {
      width: 180,
      title: "Line Name",
      field: "line",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Shift Name",
      field: "shiftName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Max Capacity",
      field: "maxCapacity",
      enableSearch: true,
      enableFilter: true,
    },
  ].filter((c) => c);

  const getShiftCapacityData = async () => {
    if (!plant.value) {
      return;
    }

    setRowsData((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      const response = await getShiftLimitsPerPlant(plant.value);

      setRowsData({
        loading: false,
        data: response.data,
        filteredData: response.data,
      });
    } catch (err) {
      showInternalError({ error: err });
    }
  };

  const openCapacityForm = (selectedRows) => {
    if (!selectedRows.length) {
      showError({
        contextText: "No Row Selected",
        info: "Please select one shift.",
      });
      return;
    }

    if (selectedRows.length > 1) {
      showError({
        contextText: "Multiple Rows Selected",
        info: "Only one shift can be updated at a time.",
      });
      return;
    }

    setSelectedShift(selectedRows[0]);
    setShowForm(true);
  };

  const closeFormPopup = () => {
    setShowForm(false);
    setSelectedShift(null);
  };

  const updateCapacity = async (newCapacity) => {
    try {
      const payload = [
        {
          id: selectedShift.id,
          newCapacity: Number(newCapacity),
        },
      ];

      await updateShiftLimits(payload);

      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: "Shift Capacity updated successfully.",
        info: `${selectedShift.shiftName} capacity updated to ${newCapacity}.`,
      });

      closeFormPopup();
      getShiftCapacityData();
    } catch (error) {
      showError({
        contextText: "Update Failed",
        info: "Unable to update shift capacity.",
      });
    }
  };

  useEffect(() => {
    handleCountChange(recordCount);
  }, [recordCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    if (plant.value) {
      getShiftCapacityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value]);

  return (
    <>
      <div className={styles.rgtTools}>
        <div className={styles.shiftCapacityToolbar}>
          <div className={styles.plantName}>Selected Plant - {plant.label}</div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={fixedColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            primaryAction={{
              name: "UPDATE CAPACITY",
              actionFn: openCapacityForm,
              authOperation: RESOURCE_OPERATION,
            }}
          />
        </div>
      </div>

      {showForm && selectedShift && (
        <CapacityForm
          selectedRecord={{
            ...selectedShift,
            name: selectedShift.shiftName,
          }}
          recordLabel="Shift Name"
          formTitle="Shift Capacity Configurator"
          closeFormPopup={closeFormPopup}
          updateCapacity={updateCapacity}
        />
      )}
    </>
  );
};

ShiftCapacity.propTypes = {
  setHasActiveEdits: PropTypes.func.isRequired,
  handleCountChange: PropTypes.func,
};

export default withAllowedOperationsProvider(
  ShiftCapacity,
  false,
  RESOURCE_TYPE.MASTER_CAPACITY
);
